import React from "react";
import { ProductType } from "./ProjectMarketplacePageWrapper";
import { COREError } from "../../COREDesignSystem/Content/COREError";
import {
  CORESelect,
  CORESelectProps,
} from "../../COREDesignSystem/Form/CORESelect";
import {
  getLabelFromProduct,
  checkIsCandleStickChart,
  Product,
  useTradableProductsForType,
} from "./PriceHistoryChartWithFilter";
import { CORELoading } from "../../COREDesignSystem/Feedback/CORELoading";
import { ProductSelectedType } from "./MarketSnapshot";
import { AuLocation } from "./PriceHistoryLocationSelect";
import {
  CORECascaderAPI,
  CORECascaderAPIProps,
} from "../../COREDesignSystem/Form/CORECascaderAPI";
import { keysToSnake } from "../../shared/global";

export const PriceHistoryProductSelect: React.FC<
  {
    productType: ProductType;
    projectMethodType?: ProductSelectedType["projectMethodType"];
  } & Omit<CORESelectProps, "mode">
> = ({ productType, projectMethodType, ...props }) => {
  const allowMultiple = !checkIsCandleStickChart(productType);

  const { sync, loading, error, tradableProducts } = useTradableProductsForType(
    productType,
    projectMethodType
  );

  if (error) {
    return <COREError />;
  }

  const options = tradableProducts?.map((tp) => {
    const label = getLabelFromProduct(tp);
    return { value: tp.id, label };
  });

  return (
    <CORELoading loading={loading && !sync} layout={"horizontal"}>
      <CORESelect
        {...props}
        options={options}
        mode={allowMultiple ? "multiple" : undefined}
      />
    </CORELoading>
  );
};

const vintageOption = (tradableProducts?: Product[]) => {
  const uniqueVintageArray = [
    ...new Map(
      tradableProducts?.map((item) => [item.vintage, item.vintage])
    ).values(),
  ];

  return uniqueVintageArray.map((vintage) => {
    return {
      label: vintage,
      value: vintage,
      children: tradableProducts?.flatMap((product) => {
        if (product.vintage === vintage) {
          return {
            label: product.shortName,
            value: product.id,
          };
        } else {
          return [];
        }
      }),
    };
  });
};

export const PriceHistoryAsxProductSelect: React.FC<
  {
    productType: ProductType;
    location?: AuLocation["shortName"];
  } & Omit<CORECascaderAPIProps<unknown, Product[]>, "endpoint" | "transform">
> = ({ productType, location, ...props }) => {
  return (
    <CORECascaderAPI
      endpoint={"tradable_products"}
      params={keysToSnake({
        select:
          "id,shortName:short_name,name_parts,vintage,isStrip:is_strip,isCap:is_cap,environmentalProduct:environmental_products!environmental_product(id,name,tenor:tenors(id,name)),project_method_types:project_method_types!project_method_type(*),cfdProduct:cfd_products!cfd_product(id,tenor:tenors(id,name),shape:shapes(id,type,region:regions(short_name))),tenor",
        order:
          "tenor_type.nullsfirst,delivery_year.nullsfirst,vintage.nullsfirst",
        productType: `eq.${productType}`,
        ...(location && {
          "name_parts->>0": `eq.${location}`,
        }),
        isActive: "eq.true",
        isStrip: "eq.false",
        certificateProject: "is.null",
        tradeType: "eq.firm",
        or: "(tenor_type.is.null,tenor_type.eq.yearly)",
        projectMethodType: "is.null",
      })}
      transform={(data: Product[]) => {
        return vintageOption(data);
      }}
      {...props}
    />
  );
};
